<template>
  <nav>

    <!-- Navigation Top -->
    <v-app-bar flat class="k-topbar" :height="72" :extension-height="mobileCrumb ? 32 : 0">

      <v-app-bar-nav-icon
        v-if="loggedIn && !$vuetify.display.mdAndUp"
        class="ml-1"
        @click="drawer = !drawer"
      />

      <CompanySwitcher
        v-if="loggedIn"
        class="ml-2"
        :companies="companies"
        :current-id="compData.id"
        :name="userData.company ? compData.name : 'Invité'"
        :avatar="userData.company ? compData.avatar || '' : ''"
        @select="changeCompany"
      />
      <v-btn v-else variant="text" to="/" class="ml-2 text-uppercase">Katana</v-btn>

      <router-link v-if="loggedIn && breadcrumb && !mobileCrumb" :to="breadcrumb.to" class="k-topbar__crumb">
        <v-icon icon="mdi-chevron-left" size="18" />
        Retour {{ breadcrumb.preposition }} {{ breadcrumb.label.toLowerCase() }}
      </router-link>

      <template v-if="mobileCrumb" #extension>
        <router-link :to="breadcrumb.to" class="k-topbar__crumb k-topbar__crumb--below">
          <v-icon icon="mdi-chevron-left" size="18" />
          Retour {{ breadcrumb.preposition }} {{ breadcrumb.label.toLowerCase() }}
        </router-link>
      </template>

      <span v-if="loggedIn && adminRoute" class="k-topbar__admin" role="status">
        <v-icon icon="mdi-shield-crown-outline" size="16" />
        <span class="k-topbar__admin-label">Mode administration</span>
      </span>

      <span v-if="loggedIn && environment" class="k-topbar__admin k-topbar__env" role="status">
        <v-icon :icon="environment.icon" size="16" />
        <span class="k-topbar__admin-label">{{ environment.label }}</span>
      </span>

      <v-spacer />

      <div v-if="loggedIn" class="k-topbar__actions mr-3">
        <div class="k-topbar__actions">
          <button
            v-if="!onTasksRoute"
            type="button"
            class="k-quick-add"
            :class="{ 'k-quick-add--icon': $vuetify.display.xs }"
            aria-label="Ajouter une tâche"
            @click="dialogs.todo = true"
          >
            <span class="k-quick-add__plus">
              <v-icon icon="mdi-plus" size="16" />
            </span>
            <span v-if="!$vuetify.display.xs" class="k-quick-add__label">Tâche</span>
          </button>
          <v-menu v-if="quickAddItems.length" location="bottom end" :offset="8" content-class="k-glass-3">
            <template v-slot:activator="{ props }">
              <button
                v-bind="props"
                type="button"
                class="k-quick-add k-quick-add--secondary"
                :class="{ 'k-quick-add--icon': $vuetify.display.xs }"
                aria-label="Ajouter"
              >
                <span class="k-quick-add__plus">
                  <v-icon icon="mdi-plus" size="16" />
                </span>
                <span v-if="!$vuetify.display.xs" class="k-quick-add__label">Ajouter</span>
                <v-icon v-if="!$vuetify.display.xs" icon="mdi-chevron-down" size="16" />
              </button>
            </template>
            <v-list class="bg-transparent" nav>
              <v-list-item
                v-for="item in quickAddItems"
                :key="item.slug"
                :prepend-icon="item.icon"
                :title="item.label"
                rounded="lg"
                @click="$router.push({ path: item.route, query: { add: 1 } })"
              />
            </v-list>
          </v-menu>
          <TaskAddDialog v-if="userData.company" v-model="dialogs.todo" :userInfo="userData" />
        </div>

        <v-menu
          v-if="userData.company"
          v-model="tasksMenu"
          location="bottom end"
          :offset="10"
          :close-on-content-click="false"
          content-class="k-glass-3 k-tasks-popover"
        >
          <template v-slot:activator="{ props }">
            <v-badge
              :content="urgentTasks"
              :model-value="urgentTasks > 0"
              color="error"
              location="top end"
              offset-x="4"
              offset-y="4"
            >
              <button
                v-bind="props"
                type="button"
                class="k-quick-add k-quick-add--secondary k-quick-add--icon"
                :class="{ 'k-quick-add--active': tasksMenu }"
                :aria-label="urgentTasks ? `Mes tâches, ${urgentTasks} à traiter` : 'Mes tâches'"
              >
                <span class="k-quick-add__plus">
                  <v-icon icon="mdi-check-circle-outline" size="18" />
                </span>
              </button>
            </v-badge>
          </template>

          <div class="k-tasks-popover__head">
            <h2 class="k-tasks-popover__title">Mes tâches</h2>
            <v-btn variant="text" size="small" color="accent" class="text-none" append-icon="mdi-arrow-right" to="/tasks" @click="tasksMenu = false">
              Tout ouvrir
            </v-btn>
          </div>
          <div class="k-tasks-popover__body">
            <TasksChecklist :userInfo="userData" :limit="8" />
          </div>
        </v-menu>

        <v-badge
          :content="notificationsStore.unreadCount"
          :model-value="notificationsStore.unreadCount > 0"
          color="info"
          location="top end"
          offset-x="2"
          offset-y="2"
        >
          <v-avatar size="36" class="cursor-pointer" @click="drawer2 = !drawer2">
            <v-img
              v-if="userData.avatar"
              cover
              :alt="`${userData.firstName}'s avatar`"
              :src="$functions.getImgLink(userData.avatar, 'medium')"
            />
            <v-icon v-else>
              mdi-account-circle
            </v-icon>
          </v-avatar>
        </v-badge>
      </div>

    </v-app-bar>

    <!-- Navigation Droite -->
    <v-navigation-drawer
      v-if="loggedIn"
      v-model="drawer2"
      class="k-drawer"
      location="end"
      floating
      temporary
      :width="400"
    >
      <div class="k-profile">
        <v-avatar size="52" class="k-profile__avatar">
          <v-img
            v-if="userData.avatar"
            cover
            :alt="`${userData.firstName}'s avatar`"
            :src="$functions.getImgLink(userData.avatar, 'medium')"
          />
          <v-icon v-else size="36">mdi-account-circle</v-icon>
        </v-avatar>
        <div class="k-profile__info">
          <div class="k-profile__name">{{ userData.firstName }} {{ userData.lastName }}</div>
          <div v-if="userData.email" class="k-profile__mail">{{ userData.email }}</div>
          <KChip v-if="userData.company" class="mt-1" color="primary" :label="compData.name" />
        </div>
      </div>

      <div class="k-profile__links">
        <v-btn
          v-for="item in items"
          :key="item.title"
          :to="item.route"
          :prepend-icon="item.icon"
          variant="tonal"
          size="large"
          density="comfortable"
          @click="drawer2 = false"
        >
          {{ item.title }}
        </v-btn>
      </div>

      <NotificationsPanel :admin-access="adminAccess" />

      <div class="k-drawer__footer">
        <v-list class="bg-transparent px-2" nav>
          <v-list-item prepend-icon="mdi-theme-light-dark" title="Thème" rounded="lg">
            <template v-slot:append>
              <DarkModeSwitch variant="segmented" />
            </template>
          </v-list-item>
          <v-list-item
            prepend-icon="mdi-help-circle-outline"
            title="Aide"
            to="/help"
            rounded="lg"
            @click="drawer2 = false"
          />
          <v-list-item
            prepend-icon="mdi-logout"
            title="Déconnexion"
            rounded="lg"
            base-color="error"
            @click="dialogs.logoutConfirm = true"
          />
        </v-list>
      </div>
    </v-navigation-drawer>

    <KDialog
      v-model="dialogs.logoutConfirm"
      title="Déconnexion"
      icon="mdi-logout"
      variant="danger"
      :max-width="420"
    >
      Voulez-vous vraiment vous déconnecter ?
      <template #actions>
        <v-btn variant="text" @click="dialogs.logoutConfirm = false">Annuler</v-btn>
        <v-btn color="error" variant="tonal" @click="signOut">Se déconnecter</v-btn>
      </template>
    </KDialog>

    <!-- Navigation Gauche -->

    <v-navigation-drawer
      v-if="loggedIn"
      v-model="drawer"
      :rail="$vuetify.display.mdAndUp"
      :expand-on-hover="$vuetify.display.mdAndUp"
      :permanent="$vuetify.display.mdAndUp"
      :temporary="!$vuetify.display.mdAndUp"
      class="k-rail"
      floating
      :order="-1"
      :rail-width="72"
    >
      <a href="/" class="k-rail__brand" aria-label="Katana" @click.prevent="$router.push('/')">
        <span class="k-brand__logo k-brand__logo--mark">
          <img src="/img/icons/android-chrome-192x192-white.png" alt="" class="k-brand__img">
        </span>
        <span class="k-rail__brand-name">Katana</span>
      </a>

      <v-list density="compact" nav>
        <v-list-item
          v-for="link in links"
          :key="link.route"
          :to="link.route"
          :prepend-icon="link.icon"
          :title="link.text"
          exact
        />
      </v-list>

      <template v-if="adminAccess">
        <v-divider />

        <v-list density="compact" nav>
          <v-list-item
            v-for="link in adminLinks"
            :key="link.text"
            :to="link.route"
            :prepend-icon="link.icon"
            :title="link.text"
            :color="adminRoute ? 'warning' : undefined"
          />
        </v-list>
      </template>
    </v-navigation-drawer>

  </nav>
</template>

<script>
import { computed } from "vue";
import { setSnackbar, useUiStore } from "@/stores/ui";
import DarkModeSwitch from "@/components/modules/@widgets/DarkModeSwitch";
import TaskAddDialog from "@/components/modules/@tasks/TaskAddDialog";
import NotificationsPanel from "@/components/NotificationsPanel.vue";
import KDialog from "@/components/ui/KDialog";
import KChip from "@/components/ui/KChip";
import CompanySwitcher from "@/components/ui/CompanySwitcher";
import TasksChecklist from "@/components/modules/@tasks/TasksChecklist";

import { useUserStore } from "@/stores/user";
import { useCompanyStore } from "@/stores/company";
import { useNotificationsStore } from "@/stores/notifications";
import { useTasksStore, dueMs } from "@/stores/tasks";
import { MODULES, hasModule } from "@/utils/modules";
import { getEnvironment } from "@/utils/environment";
import { captureException, reset as resetPostHog } from "@/posthog";

export default {
  props: {
    id: {
      type: String,
    },
  },
  components: {
    DarkModeSwitch,
    TaskAddDialog,
    NotificationsPanel,
    KDialog,
    KChip,
    CompanySwitcher,
    TasksChecklist,
  },
  setup() {
    const userStore = useUserStore();
    const companyStore = useCompanyStore();
    const notificationsStore = useNotificationsStore();
    const tasksStore = useTasksStore();
    const breadcrumb = computed(() => useUiStore().breadcrumb);
    return { userStore, companyStore, notificationsStore, tasksStore, breadcrumb };
  },
  data() {
    const itemsList = this.$firebase.auth().currentUser
      ? [{
        title: "Profil",
        icon: "mdi-account",
        route: "/profile",
      }, {
        title: "Mon Entreprise",
        icon: "mdi-office-building-outline",
        route: "/my-company",
      }]
      : [{
        title: "Se connecter",
        icon: "mdi-login",
        route: "/login",
      }];

    return {
      dialogs: {
        todo: false,
        genQR: false,
        logoutConfirm: false,
      },

      drawer: false,
      drawer2: false,
      tasksMenu: false,

      darkMode: false,

      links: [],
      adminLinks: [{
        icon: "mdi-shield-key-outline",
        text: "Administration",
        route: "/administration",
      }, {
        icon: "mdi-package-variant-closed",
        text: "Catalogue",
        route: "/administration/catalog",
      }, {
        icon: "mdi-bell-badge-outline",
        text: "Notifications",
        route: "/administration/notifications",
      }, {
        icon: "mdi-palette-outline",
        text: "Design System",
        route: "/administration/design-system",
      }, {
        icon: "mdi-flask-outline",
        text: "Sandbox",
        route: "/administration/sandbox",
      }],

      items: itemsList,
      companies: [],
      adminAccess: false,
      adminListenerUnsubscribe: null,
    };
  },
  computed: {
    quickAddItems() {
      return ["quotes", "invoices", "customers", "products"]
        .filter((slug) => hasModule(this.companyStore.modules, slug))
        .map((slug) => MODULES[slug]);
    },
    urgentTasks() {
      const endOfDay = new Date().setHours(23, 59, 59, 999);
      return this.tasksStore.list.filter((task) => Number(task.status) < 3 && dueMs(task.dueDate) <= endOfDay).length;
    },
    loggedIn() {
      return !!this.userStore.user; // Or check auth currentUser
    },
    onTasksRoute() {
      return String(this.$route.name || "").toLowerCase().includes("task");
    },
    environment() {
      return getEnvironment();
    },
    adminRoute() {
      return this.$route.path.startsWith("/administration");
    },
    mobileCrumb() {
      return this.loggedIn && !!this.breadcrumb && this.$vuetify.display.xs;
    },
    userData() {
      return this.userStore.user || this.$models.user;
    },
    compData() {
      return this.companyStore.company || this.$models.company;
    },
  },
  watch: {
    "userStore.user": {
      handler(user) {
        if (user) {
          this.setupAdminListener(user.id);
          this.loadCompaniesList(user.id);
        } else {
          this.adminAccess = false;
          this.companies = [];
          this.resetNav();

          if (typeof this.adminListenerUnsubscribe === "function")
            this.adminListenerUnsubscribe();


          this.adminListenerUnsubscribe = null;
        }
      },
      immediate: true,
    },
    "companyStore.company": {
      handler(company) {
        this.resetNav();
        this.buildNavLinks();
        const companyId = company?.id || this.userStore.user?.company;
        if (companyId) {
          this.notificationsStore.ensureWatch(companyId);
          this.tasksStore.ensureWatch(companyId);
        } else this.notificationsStore.resetState();
      },
      immediate: true,
    },
    "userStore.user.company"(companyId) {
      if (companyId) {
        this.notificationsStore.ensureWatch(companyId);
        this.tasksStore.ensureWatch(companyId);
      }
    },
    "companyStore.modules": {
      handler() {
        if (!this.companyStore.loading) {
          this.resetNav();
          this.buildNavLinks();
        }
      },
      deep: true,
      immediate: true,
    },
  },
  created() {
    this.drawer = this.$vuetify.display.mdAndUp;
    this.changeDarkTheme();
  },
  methods: {
    changeDarkTheme() {
      const favicon = document.getElementById("favicon");

      this.darkMode = this.$vuetify.theme.current.dark;

      if (this.darkMode) favicon.href = "https://cdn.infographart.com/logos/KATANA/favicon.ico";
    },
    resetNav() {
      this.links = [{
        icon: "mdi-view-dashboard",
        text: "Tableau de bord",
        route: "/",
        id: 0,
      }];
    },
    setupAdminListener(uid) {
      if (typeof this.adminListenerUnsubscribe === "function")
        this.adminListenerUnsubscribe();


      this.$api.apiUser({ url: `/users/${uid}/ranks/BzAcMoJjpbCaNFyQSJ3N` }).then((res) => {
        this.adminAccess = !!res.data.exists;
      }).catch(() => {
        this.adminAccess = false;
      });
    },
    loadCompaniesList(uid) {
      this.companies = [];

      this.$api.apiUser({
        method: "get",
        url: `/users/${uid}/companies`,
      }).then((res) => {
        this.companies = (res.data.documents || [])
          .map((company) => ({
            avatar: company.avatar || "",
            name: company.name || "Inconnu",
            email: company.email || "",
            website: company.website || "",
            id: company.id,
          }))
          .sort((a, b) => a.name.toUpperCase().localeCompare(b.name.toUpperCase()));
      });
    },
    buildNavLinks() {
      if (this.companyStore.loading) return;

      const order = ["customers", "companies", "quotes", "invoices", "logistics", "projects", "products", "blogs", "teams"];

      order.forEach((slug) => {
        const mod = MODULES[slug];
        this.controlNavLinks(mod.id, { icon: mod.icon, text: mod.label, route: mod.route });
      });

      const statsModules = ["invoices", "quotes", "contacts", "projects"];
      if (statsModules.some((slug) => hasModule(this.companyStore.modules, slug)))
        this.links.push({
          icon: "mdi-chart-box-outline",
          text: "Statistiques",
          route: "/statistics",
          id: "statistics",
        });

      this.links.push({
        icon: "mdi-wrench",
        text: "Outils",
        route: "/tools",
        id: "outils",
      });
    },
    controlNavLinks(module, linkToAdd) {
      if (!this.companyStore.modules) return;

      const hasAccess = this.companyStore.modules.some(m => m.id === module);
      const index = this.links.findIndex(l => l.route === linkToAdd.route);

      if (hasAccess && index === -1)
        this.links.push({
          ...linkToAdd,
          id: module,
        });
    },
    async changeCompany({ id: compID, name: compName }) {
      this.drawer2 = false;

      if (!this.$route.meta.safePlace)
        await this.$router.replace({
          path: "/",
        });

      // Use Store Action
      this.userStore.updateUserCompany(compID).then(() => {
        setSnackbar({
          text: `Votre entreprise active est désormais ${compName}.`,
          color: "info",
        });
      }).catch((err) => {
        setSnackbar({
          text: err.message || err,
          color: "error",
        });
      });
    },
    async signOut() {
      try {
        await this.$firebase.auth().signOut();
        this.$api.clearApiSession();
        resetPostHog();
        this.userStore.stopUserListener();
        this.companyStore.stopCompanyListener();

        this.$router.replace({
          path: "/login",
        });
      } catch (err) {
        console.log(err);
        captureException(err, {
          flow: "sign_out",
        });
      }
    },
  },
  beforeUnmount() {
    if (typeof this.adminListenerUnsubscribe === "function")
      this.adminListenerUnsubscribe();


    this.adminListenerUnsubscribe = null;
  },
};
</script>
